import { ChevronDown, Drama, Icon } from "@norican/icons";
import { Button, cn, Menu } from "@norican/react";
import { Link } from "react-router";

import { useT } from "@/i18n";
import { useMe } from "@/lib/api";
import { DEMO_ROLE_COLOUR, type DemoRole, mayOfferDemoAs } from "@/lib/demo-persona";
import { useDemoPersona } from "@/lib/demo-persona-store";

import { useBackToMe, useDemoPersonas, useStartDemoPersona } from "./api";

/** The setup page: the cast, someone else, prepare and reset. */
export const DEMO_PERSONA_PATH = "/admin/demo-personas";

/**
 * "Demo as…" (19 Oct): pick the role THIS tab plays. In the header for an
 * admin on VIEW_AS_EMAILS on a test system (never LIVE); on a persona's
 * banner as "Switch persona". Other tabs are untouched.
 */
export function DemoAsMenu({ label, onBanner = false }: { label?: string; onBanner?: boolean }) {
  const { t } = useT();
  const { data: me } = useMe();
  const persona = useDemoPersona();
  const offered = mayOfferDemoAs(me);
  const cast = useDemoPersonas(offered);
  const start = useStartDemoPersona();
  const backToMe = useBackToMe();

  if (!offered || (!onBanner && persona !== null)) {
    return null;
  }
  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          <Button
            variant={onBanner ? "outline" : "ghost"}
            tone="neutral"
            size="sm"
            className={onBanner ? "bg-bg text-fg" : "max-sm:gap-0 max-sm:px-2"}
            startIcon={<Icon icon={Drama} size="sm" />}
            endIcon={<Icon icon={ChevronDown} size="sm" />}
            aria-label={label ?? t("demoPersona.menu")}
            disabled={start.isPending}
          />
        }
      >
        <span className={onBanner ? undefined : "hidden lg:inline"}>
          {label ?? t("demoPersona.menu")}
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={6} align="end">
          <Menu.Popup width="content">
            <Menu.Group>
              <Menu.GroupLabel>{t("demoPersona.thisTab")}</Menu.GroupLabel>
              {(cast.data?.items ?? []).map((p) => (
                <Menu.Item
                  key={p.role}
                  disabled={!p.employee_id}
                  onClick={() => {
                    if (p.employee_id) {
                      start.mutate({ employeeId: p.employee_id, role: p.role as DemoRole });
                    }
                  }}
                >
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={cn("inline-block size-3 rounded-full", DEMO_ROLE_COLOUR[p.role])}
                    />
                    <span className="font-semibold">{t(`demoPersona.role.${p.role}`)}</span>
                    <span className="text-fg-muted">
                      {p.employee_id ? p.name : t("demoPersona.missing", { name: p.name })}
                    </span>
                  </span>
                </Menu.Item>
              ))}
              {cast.isLoading ? <Menu.Item disabled>{t("demoPersona.loading")}</Menu.Item> : null}
            </Menu.Group>
            <Menu.Separator />
            <Menu.Item render={<Link to={DEMO_PERSONA_PATH} />}>{t("demoPersona.setup")}</Menu.Item>
            {persona !== null ? (
              <Menu.Item onClick={backToMe}>{t("demoPersona.backToMe")}</Menu.Item>
            ) : null}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
